import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { notifyError } from '../../utils/notifyError'

/**
 * ============================================================================
 * QueryClient (single instance)
 * ============================================================================
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      retryDelay: attemptIndex =>
        Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false
    }
  }
})

/**
 * ============================================================================
 * Global error handling (TanStack Query v5)
 * ============================================================================
 *
 * Subscriptions fire many times.
 * We notify ONLY once per unique error object.
 */
const notifiedErrors = new WeakSet<object>()

/**
 * -------------------------
 * Query errors
 * -------------------------
 */
queryClient.getQueryCache().subscribe(event => {
  const query = event?.query
  if (!query) return

  if (query.state.status !== 'error') return
  if (query.meta?.silent) return

  const error = query.state.error
  if (!error || typeof error !== 'object') return

  if (notifiedErrors.has(error)) return
  notifiedErrors.add(error)

  notifyError(error)
})

/**
 * -------------------------
 * Mutation errors
 * -------------------------
 */
queryClient.getMutationCache().subscribe(event => {
  const mutation = event?.mutation
  if (!mutation) return

  if (mutation.state.status !== 'error') return
  if (mutation.meta?.silent) return

  const error = mutation.state.error
  if (!error || typeof error !== 'object') return

  if (notifiedErrors.has(error)) return
  notifiedErrors.add(error)

  notifyError(error)
})

/**
 * ============================================================================
 * Provider component
 * ============================================================================
 */
interface ReactQueryProviderProps {
  children: React.ReactNode
}

export const ReactQueryProvider: React.FC<ReactQueryProviderProps> = ({
  children
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
